import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker, Button, Table, Divider, Tag, message, Tooltip, Radio } from 'antd';
import { apiForteSettlementSummary, apiForteSettlementTransactions } from 'services/dashboardService';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { RangePicker } = DatePicker;

const formatCurrency = (val) => {
    if (val == null || val === '') return '';
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
};

const renderMissing = (value) => {
    if (value == null || String(value).trim() === '') {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>Missing</span>;
    }
    return value;
};

const hasFundingId = (record) => {
    const raw = record?.forte_funding_id;
    if (raw == null) return false;
    const normalized = String(raw).trim().toLowerCase();
    if (!normalized) return false;
    return !['null', 'undefined', 'n/a', 'na', '-'].includes(normalized);
};

const renderFee = (val) => val != null ? formatCurrency(val) : <span style={{ color: '#bbb' }}>—</span>;

const renderDiscrepancy = (val) => {
    if (val == null) return <span style={{ color: '#bbb' }}>—</span>;
    if (val === 0)   return <span style={{ color: '#52c41a' }}>$0.00</span>;
    return <span style={{ color: val > 0 ? '#fa8c16' : '#ff4d4f' }}>{formatCurrency(val)}</span>;
};

const isAch = (record) => {
    const type = (record?.forte_settle_type || '').toLowerCase();
    const pm   = (record?.payment_method   || '').toLowerCase();
    return pm === 'echeck' || type.includes('echeck') || type.includes('ach');
};

const renderBatchId = (value, record) => {
    if (value != null && String(value).trim() !== '') return value;
    if (isAch(record)) return <span style={{ color: '#722ed1', fontStyle: 'italic' }}>ACH</span>;
    return <span style={{ color: '#999', fontStyle: 'italic' }}>Missing</span>;
};

const renderForteNet = (val, record) => {
    const net   = val != null ? formatCurrency(val) : <span style={{ color: '#bbb' }}>—</span>;
    const batch = record?.batch_settle;
    const showHint = batch != null && parseFloat(batch) !== parseFloat(val);
    return (
        <div>
            <div>{net}</div>
            {showHint && (
                <Tooltip title="Full bank deposit for this funding batch (includes acknowledged adjustments)">
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1 }}>
                        Bank: {formatCurrency(batch)}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

const renderAckedRefund = (val) => {
    const num = parseFloat(val);
    if (!val || isNaN(num) || num === 0) return null;
    return <span style={{ color: '#ff4d4f' }}>-{formatCurrency(num)}</span>;
};

const renderAckedDeposit = (val) => {
    const num = parseFloat(val);
    if (!val || isNaN(num) || num === 0) return null;
    return <span style={{ color: '#52c41a' }}>{formatCurrency(num)}</span>;
};

const renderIssuedRefund = (val) => {
    const num = parseFloat(val);
    if (!val || isNaN(num) || num === 0) return null;
    return <span style={{ color: '#ff4d4f' }}>-{formatCurrency(num)}</span>;
};

const renderBatchSettle = (val, record) => {
    if (val == null) return <span style={{ color: '#bbb' }}>—</span>;
    const hasAdjustments =
        (parseFloat(record.acked_refund)  || 0) !== 0 ||
        (parseFloat(record.acked_deposit) || 0) !== 0;
    return (
        <span style={{ fontWeight: hasAdjustments ? 600 : 400, color: hasAdjustments ? '#fa8c16' : 'inherit' }}>
            {formatCurrency(val)}
            {hasAdjustments && (
                <Tooltip title="Includes acknowledged adjustments (refunds, chargebacks, or extra deposits)">
                    <span style={{ marginLeft: 4, fontSize: 11, color: '#fa8c16' }}>●</span>
                </Tooltip>
            )}
        </span>
    );
};

const SUMMARY_COLUMNS_BASIC = [
    { title: 'Settle Date',  dataIndex: 'forte_settle_date',     key: 'forte_settle_date',     width: 120 },
    { title: 'Funding ID',   dataIndex: 'forte_funding_id',      key: 'forte_funding_id',      width: 200, ellipsis: true, render: renderMissing },
    { title: 'Batch ID',     dataIndex: 'forte_settle_batch_id', key: 'forte_settle_batch_id', width: 130, render: renderBatchId },
    { title: 'Count',        dataIndex: 'transaction_count',     key: 'transaction_count',     width: 80,  align: 'right' },
    { title: 'Our Settle',   dataIndex: 'forte_net_amount',      key: 'forte_net_amount',      width: 120, align: 'right', render: renderFee },
    { title: 'Bank Deposit', dataIndex: 'batch_settle',          key: 'batch_settle',          width: 120, align: 'right', render: renderBatchSettle },
    { title: 'Fee Diff',     dataIndex: 'fee_discrepancy',       key: 'fee_discrepancy',       width: 100, align: 'right', render: renderDiscrepancy },
];

const SUMMARY_COLUMNS_ADVANCED = [
    { title: 'Settle Date',      dataIndex: 'forte_settle_date',     key: 'forte_settle_date',     width: 120 },
    { title: 'Funding ID',       dataIndex: 'forte_funding_id',      key: 'forte_funding_id',      width: 200, ellipsis: true, render: renderMissing },
    { title: 'Batch ID',         dataIndex: 'forte_settle_batch_id', key: 'forte_settle_batch_id', width: 130, render: renderBatchId },
    { title: 'Type',             dataIndex: 'forte_settle_type',     key: 'forte_settle_type',     width: 90 },
    { title: 'Transactions',     dataIndex: 'transaction_count',     key: 'transaction_count',     width: 110, align: 'right' },
    { title: 'Gross',            dataIndex: 'gross_amount',          key: 'gross_amount',          width: 120, align: 'right', render: formatCurrency },
    { title: 'BeClutch Net',     dataIndex: 'our_net_amount',        key: 'our_net_amount',        width: 110, align: 'right', render: formatCurrency },
    { title: 'Forte Fee',        dataIndex: 'effective_fee_amount',  key: 'effective_fee_amount',  width: 110, align: 'right', render: renderFee },
    { title: 'Our Settle',       dataIndex: 'forte_net_amount',      key: 'forte_net_amount',      width: 120, align: 'right', render: renderForteNet },
    { title: 'Acked Refunds',    dataIndex: 'acked_refund',          key: 'acked_refund',          width: 120, align: 'right', render: renderAckedRefund },
    { title: 'Extra Deposits',   dataIndex: 'acked_deposit',         key: 'acked_deposit',         width: 120, align: 'right', render: renderAckedDeposit },
    { title: 'Issued Refunds',   dataIndex: 'reg_refund_sum',        key: 'reg_refund_sum',        width: 120, align: 'right', render: renderIssuedRefund },
    { title: 'Bank Deposit',     dataIndex: 'batch_settle',          key: 'batch_settle',          width: 120, align: 'right', render: renderBatchSettle },
    { title: 'Fee Diff',         dataIndex: 'fee_discrepancy',       key: 'fee_discrepancy',       width: 100, align: 'right', render: renderDiscrepancy },
];

const DETAIL_COLUMNS_BASIC = [
    { title: 'Txn Date',       dataIndex: 'transaction_date',    key: 'transaction_date',    width: 110 },
    { title: 'Transaction ID', dataIndex: 'transaction_id',      key: 'transaction_id',      width: 170, ellipsis: true },
    { title: 'Payer',          dataIndex: 'payer_name',          key: 'payer_name',          width: 160 },
    { title: 'Email',          dataIndex: 'p_email',             key: 'p_email',             width: 200, ellipsis: true },
    { title: 'Registration',   dataIndex: 'registration_name',   key: 'registration_name',   ellipsis: true },
    { title: 'Charged',        dataIndex: 'total_checkout',      key: 'total_checkout',      width: 110, align: 'right', render: formatCurrency },
    { title: 'Forte Net',      dataIndex: 'forte_net',           key: 'forte_net',           width: 110, align: 'right', render: formatCurrency },
    { title: 'Forte Fee',      dataIndex: 'forte_fee',           key: 'forte_fee',           width: 100, align: 'right', render: renderFee },
    { title: 'Fee Diff',       dataIndex: 'fee_discrepancy',     key: 'fee_discrepancy',     width: 95,  align: 'right', render: renderDiscrepancy },
    { title: 'Type',           dataIndex: 'forte_settle_type',   key: 'forte_settle_type',   width: 90 },
];

const DETAIL_COLUMNS_ADVANCED = [
    { title: 'Txn Date',       dataIndex: 'transaction_date',    key: 'transaction_date',    width: 110 },
    { title: 'Transaction ID', dataIndex: 'transaction_id',      key: 'transaction_id',      width: 170, ellipsis: true },
    { title: 'Payer',          dataIndex: 'payer_name',          key: 'payer_name',          width: 160 },
    { title: 'Email',          dataIndex: 'p_email',             key: 'p_email',             width: 200, ellipsis: true },
    { title: 'Registration',   dataIndex: 'registration_name',   key: 'registration_name',   ellipsis: true },
    { title: 'Charged',        dataIndex: 'total_checkout',      key: 'total_checkout',      width: 110, align: 'right', render: formatCurrency },
    { title: 'Forte Net',      dataIndex: 'forte_net',           key: 'forte_net',           width: 110, align: 'right', render: formatCurrency },
    { title: 'Forte Fee',      dataIndex: 'effective_fee',       key: 'effective_fee',       width: 100, align: 'right', render: renderFee },
    { title: 'Fee Diff',       dataIndex: 'fee_discrepancy',     key: 'fee_discrepancy',     width: 95,  align: 'right', render: renderDiscrepancy },
    { title: 'Type',           dataIndex: 'forte_settle_type',   key: 'forte_settle_type',   width: 90 },
];

const ACK_COLUMNS = [
    {
        title: 'Type', dataIndex: 'entry_type', key: 'entry_type', width: 130,
        render: (val) => {
            const config = {
                manual_charge:    { color: 'blue',   label: 'Manual Charge' },
                refund:           { color: 'orange', label: 'Refund' },
                chargeback:       { color: 'red',    label: 'Chargeback' },
                dispute_recovery: { color: 'green',  label: 'Recovery' },
            };
            const c = config[val] || { color: 'default', label: val || '—' };
            return <Tag color={c.color}>{c.label}</Tag>;
        },
    },
    { title: 'Name',     dataIndex: 'name',          key: 'name',          ellipsis: true },
    { title: 'Date',     dataIndex: 'received_date', key: 'received_date', width: 110 },
    {
        title: 'Amount', dataIndex: 'amount', key: 'amount', width: 130, align: 'right',
        render: (val, record) => {
            const num = parseFloat(val);
            if (isNaN(num)) return val;
            const isRefund = num < 0;
            const typeLabel = {
                manual_charge:    'deposit',
                refund:           'refund',
                chargeback:       'chargeback',
                dispute_recovery: 'recovery',
            }[record?.entry_type] || (isRefund ? 'refund' : 'deposit');
            return (
                <span style={{ color: isRefund ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
                    {isRefund ? '-' : '+'}{formatCurrency(Math.abs(num))}
                    <span style={{ fontWeight: 400, color: '#8c8c8c', marginLeft: 4 }}>
                        ({typeLabel})
                    </span>
                </span>
            );
        },
    },
    { title: 'Notes',    dataIndex: 'notes',         key: 'notes',         ellipsis: true },
];

const PARTICIPANT_COLUMNS = [
    { title: 'Name',     key: 'name',          width: 180,
      render: (_, p) => `${p.camper_first_name || ''} ${p.camper_last_name || ''}`.trim() || '—' },
    { title: 'Email',    dataIndex: 'camper_email', key: 'camper_email', width: 200, ellipsis: true },
    { title: 'Programs', key: 'programs',
      render: (_, p) => {
          if (!p.programs || p.programs.length === 0) return <span style={{ color: '#999' }}>—</span>;
          return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {p.programs.map((prog, i) => (
                      <Tag key={i} color="blue" style={{ marginBottom: 2 }}>
                          {prog.program_name}
                          {prog.price_at_purchase > 0 ? ` (${formatCurrency(prog.price_at_purchase)})` : ''}
                      </Tag>
                  ))}
              </div>
          );
      }
    },
];

const ExpandedRow = ({ record }) => {
    const participants = record.participants || [];
    if (participants.length === 0) {
        return <div style={{ padding: '8px 16px', color: '#999', fontStyle: 'italic' }}>No participant records found.</div>;
    }
    return (
        <div style={{ padding: '8px 16px' }}>
            <Table
                dataSource={participants}
                columns={PARTICIPANT_COLUMNS}
                rowKey={(p, i) => `${p.camper_first_name}-${p.camper_last_name}-${i}`}
                pagination={false}
                size="small"
                bordered={false}
                style={{ background: '#fafafa' }}
                rowClassName={(_, idx) => idx % 2 === 1 ? 'table-row-light' : ''}
            />
        </div>
    );
};

function ForteSettlementReport() {
    const userDataStore = useSelector((x) => x.userDataStore);
    const appDataStore  = useSelector((x) => x.appDataStore);

    const [viewMode, setViewMode]               = useState('basic');
    const [dateRange, setDateRange]             = useState(null);
    const [isBusy, setIsBusy]                   = useState(false);
    const [summaryData, setSummaryData]         = useState(null);
    const [selectedFunding, setSelectedFunding] = useState(null);
    const [selectedSettlement, setSelectedSettlement] = useState(null);
    const [detailRows, setDetailRows]           = useState(null);
    const [batchAcknowledged, setBatchAcknowledged] = useState(null);
    const [detailLoading, setDetailLoading]     = useState(false);
    const [exportAllLoading, setExportAllLoading] = useState(false);

    const clubId = appDataStore?.club?.id;

    const handleRun = () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning('Please select a date range.');
            return;
        }

        const date_from = dateRange[0].format('YYYY-MM-DD');
        const date_to   = dateRange[1].format('YYYY-MM-DD');

        setIsBusy(true);
        setSummaryData(null);
        setSelectedFunding(null);
        setSelectedSettlement(null);
        setDetailRows(null);
        setBatchAcknowledged(null);

        apiForteSettlementSummary({
            token:    userDataStore.token,
            club_id:  clubId,
            date_from,
            date_to,
        })
            .then((res) => {
                if (res) {
                    setSummaryData(res);
                    if (res.settlements?.length === 0) {
                        message.info('No settlement data found for that date range.');
                    }
                } else {
                    message.info('No settlement data found.');
                }
                setIsBusy(false);
            })
            .catch(() => {
                message.error('Failed to load settlement data.');
                setIsBusy(false);
            });
    };

    const handleRowClick = (record) => {
        const fundingId = record.forte_funding_id;
        if (!hasFundingId(record)) {
            message.info('This settlement row is missing a Funding ID, so transaction drill-down is unavailable.');
            return;
        }
        if (selectedFunding === fundingId) {
            setSelectedFunding(null);
            setSelectedSettlement(null);
            setDetailRows(null);
            setBatchAcknowledged(null);
            return;
        }
        setSelectedFunding(fundingId);
        setSelectedSettlement(record);
        setDetailRows(null);
        setBatchAcknowledged(null);
        setDetailLoading(true);

        apiForteSettlementTransactions({
            token:      userDataStore.token,
            club_id:    clubId,
            funding_id: fundingId,
        })
            .then((res) => {
                setDetailRows(res?.transactions || []);
                setBatchAcknowledged(res?.batch_acknowledged || []);
                setDetailLoading(false);
            })
            .catch(() => {
                message.error('Failed to load transaction detail.');
                setDetailLoading(false);
            });
    };

    const totals = summaryData?.totals;

    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();

        const summaryRows = [
            ['Forte Settlement Report'],
            [`Period: ${summaryData.date_from} to ${summaryData.date_to}`],
            [],
            ['Settle Date', 'Funding ID', 'Batch ID', 'Type', 'Transactions', 'Gross', 'BeClutch Net', 'Forte Fee', 'Our Settle', 'Acked Refunds', 'Extra Deposits', 'Issued Refunds', 'Bank Deposit', 'Fee Diff'],
            ...summaryData.settlements.map((r) => [
                r.forte_settle_date,
                r.forte_funding_id,
                r.forte_settle_batch_id || (isAch(r) ? 'ACH' : ''),
                r.forte_settle_type,
                Number(r.transaction_count),
                parseFloat(r.gross_amount)            || 0,
                parseFloat(r.our_net_amount)          || 0,
                r.effective_fee_amount != null ? parseFloat(r.effective_fee_amount) : '',
                r.forte_net_amount     != null ? parseFloat(r.forte_net_amount)     : '',
                r.acked_refund    != null && parseFloat(r.acked_refund)    !== 0 ? -Math.abs(parseFloat(r.acked_refund))    : '',
                r.acked_deposit   != null && parseFloat(r.acked_deposit)   !== 0 ? parseFloat(r.acked_deposit)              : '',
                r.reg_refund_sum  != null && parseFloat(r.reg_refund_sum)  !== 0 ? -Math.abs(parseFloat(r.reg_refund_sum))  : '',
                r.batch_settle    != null ? parseFloat(r.batch_settle)    : '',
                r.fee_discrepancy != null ? parseFloat(r.fee_discrepancy) : '',
            ]),
            [],
            ['', '', '', 'TOTALS', Number(totals.transaction_count),
                parseFloat(totals.gross_amount)   || 0,
                parseFloat(totals.our_net_amount) || 0,
                totals.effective_fee_amount != null ? parseFloat(totals.effective_fee_amount) : '',
                totals.forte_net_amount     != null ? parseFloat(totals.forte_net_amount)     : '',
                totals.acked_refund   != null && parseFloat(totals.acked_refund)   !== 0 ? -Math.abs(parseFloat(totals.acked_refund))   : '',
                totals.acked_deposit  != null && parseFloat(totals.acked_deposit)  !== 0 ? parseFloat(totals.acked_deposit)             : '',
                totals.reg_refund_sum != null && parseFloat(totals.reg_refund_sum) !== 0 ? -Math.abs(parseFloat(totals.reg_refund_sum)) : '',
                totals.batch_settle   != null ? parseFloat(totals.batch_settle)   : '',
                totals.fee_discrepancy != null ? parseFloat(totals.fee_discrepancy) : '',
            ],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

        if (detailRows && detailRows.length > 0) {
            const ws2 = XLSX.utils.aoa_to_sheet([
                [`Transactions — Funding ID: ${selectedFunding}`],
                [],
                ['Txn Date', 'Transaction ID', 'Payer', 'Email', 'Registration', 'Charged', 'Forte Fee', 'Settled', 'Type'],
                ...detailRows.map((r) => [
                    r.transaction_date,
                    r.transaction_id,
                    r.payer_name,
                    r.p_email,
                    r.registration_name,
                    parseFloat(r.total_checkout) || 0,
                    r.effective_fee != null ? parseFloat(r.effective_fee) : '',
                    parseFloat(r.forte_settle_amount) || 0,
                    r.forte_settle_type,
                ]),
            ]);
            XLSX.utils.book_append_sheet(wb, ws2, 'Transactions');
        }

        if (batchAcknowledged && batchAcknowledged.length > 0) {
            const ws3 = XLSX.utils.aoa_to_sheet([
                [`Acknowledged Adjustments — Funding ID: ${selectedFunding}`],
                [],
                ['Type', 'Name', 'Date', 'Amount', 'Notes', 'Transaction ID'],
                ...batchAcknowledged.map((r) => [
                    r.entry_type || '',
                    r.name,
                    r.received_date,
                    parseFloat(r.amount) || 0,
                    r.notes || '',
                    r.transaction_id || '',
                ]),
            ]);
            XLSX.utils.book_append_sheet(wb, ws3, 'Adjustments');
        }

        XLSX.writeFile(wb, `forte-settlement-${summaryData.date_from}-to-${summaryData.date_to}.xlsx`);
    };

    const handleExportPdf = async () => {
        const logoDataUrl = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d').drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
            img.src = `${process.env.PUBLIC_URL}/assets/images/logo-large.png`;
        });

        const doc = new jsPDF({ orientation: 'landscape' });
        const pageWidth = doc.internal.pageSize.getWidth();

        const addLogo = () => {
            if (logoDataUrl) {
                doc.addImage(logoDataUrl, 'PNG', pageWidth - 52, 4, 42, 14);
            }
        };

        addLogo();
        doc.setFontSize(16);
        doc.text('Forte Settlement Report', 14, 16);
        doc.setFontSize(10);
        doc.text(`Period: ${summaryData.date_from} to ${summaryData.date_to}`, 14, 24);
        doc.text(
            `Batches: ${summaryData.settlements.length}  |  Transactions: ${totals.transaction_count}  |  Gross: ${formatCurrency(totals.gross_amount)}  |  BeClutch Net: ${formatCurrency(totals.our_net_amount)}  |  Bank Deposit: ${totals.batch_settle != null ? formatCurrency(totals.batch_settle) : '—'}`,
            14, 30
        );

        autoTable(doc, {
            startY: 36,
            head: [['Settle Date', 'Funding ID', 'Batch ID', 'Type', 'Txns', 'Gross', 'BeClutch Net', 'Forte Fee', 'Our Settle', 'Acked Refunds', 'Extra Dep.', 'Issued Refunds', 'Bank Deposit', 'Fee Diff']],
            body: [
                ...summaryData.settlements.map((r) => [
                    r.forte_settle_date,
                    r.forte_funding_id,
                    r.forte_settle_batch_id || (isAch(r) ? 'ACH' : ''),
                    r.forte_settle_type,
                    r.transaction_count,
                    formatCurrency(r.gross_amount),
                    formatCurrency(r.our_net_amount),
                    r.effective_fee_amount != null ? formatCurrency(r.effective_fee_amount) : '—',
                    r.forte_net_amount     != null ? formatCurrency(r.forte_net_amount)     : '—',
                    r.acked_refund   && parseFloat(r.acked_refund)   !== 0 ? `-${formatCurrency(parseFloat(r.acked_refund))}`   : '',
                    r.acked_deposit  && parseFloat(r.acked_deposit)  !== 0 ? formatCurrency(r.acked_deposit)                   : '',
                    r.reg_refund_sum && parseFloat(r.reg_refund_sum) !== 0 ? `-${formatCurrency(parseFloat(r.reg_refund_sum))}` : '',
                    r.batch_settle    != null ? formatCurrency(r.batch_settle)    : '—',
                    r.fee_discrepancy != null ? formatCurrency(r.fee_discrepancy) : '—',
                ]),
                ['', '', '', 'TOTALS', totals.transaction_count,
                    formatCurrency(totals.gross_amount),
                    formatCurrency(totals.our_net_amount),
                    totals.effective_fee_amount != null ? formatCurrency(totals.effective_fee_amount) : '—',
                    totals.forte_net_amount     != null ? formatCurrency(totals.forte_net_amount)     : '—',
                    totals.acked_refund   && parseFloat(totals.acked_refund)   !== 0 ? `-${formatCurrency(parseFloat(totals.acked_refund))}`   : '',
                    totals.acked_deposit  && parseFloat(totals.acked_deposit)  !== 0 ? formatCurrency(totals.acked_deposit)                   : '',
                    totals.reg_refund_sum && parseFloat(totals.reg_refund_sum) !== 0 ? `-${formatCurrency(parseFloat(totals.reg_refund_sum))}` : '',
                    totals.batch_settle    != null ? formatCurrency(totals.batch_settle)    : '—',
                    totals.fee_discrepancy != null ? formatCurrency(totals.fee_discrepancy) : '—',
                ],
            ],
            styles: { fontSize: 7 },
            headStyles: { fillColor: [24, 144, 255] },
            footStyles: { fontStyle: 'bold' },
        });

        if (detailRows && detailRows.length > 0) {
            doc.addPage();
            addLogo();
            doc.setFontSize(14);
            doc.text(`Transactions — Funding ID: ${selectedFunding}`, 14, 16);
            autoTable(doc, {
                startY: 22,
                head: [['Txn Date', 'Transaction ID', 'Payer', 'Email', 'Registration', 'Charged', 'Forte Fee', 'Settled', 'Type']],
                body: detailRows.map((r) => [
                    r.transaction_date,
                    r.transaction_id,
                    r.payer_name,
                    r.p_email,
                    r.registration_name,
                    formatCurrency(r.total_checkout),
                    r.effective_fee != null ? formatCurrency(r.effective_fee) : '—',
                    formatCurrency(r.forte_settle_amount),
                    r.forte_settle_type,
                ]),
                styles: { fontSize: 7 },
                headStyles: { fillColor: [24, 144, 255] },
            });
        }

        if (batchAcknowledged && batchAcknowledged.length > 0) {
            doc.addPage();
            addLogo();
            doc.setFontSize(14);
            doc.text(`Acknowledged Adjustments — Funding ID: ${selectedFunding}`, 14, 16);
            autoTable(doc, {
                startY: 22,
                head: [['Type', 'Name', 'Date', 'Amount', 'Notes']],
                body: batchAcknowledged.map((r) => {
                    const num = parseFloat(r.amount);
                    const typeLabel = {
                        manual_charge:    'deposit',
                        refund:           'refund',
                        chargeback:       'chargeback',
                        dispute_recovery: 'recovery',
                    }[r.entry_type] || (num < 0 ? 'refund' : 'deposit');
                    return [
                        r.entry_type || '',
                        r.name,
                        r.received_date,
                        `${num < 0 ? '-' : '+'}${formatCurrency(Math.abs(num))} (${typeLabel})`,
                        r.notes || '',
                    ];
                }),
                styles: { fontSize: 7 },
                headStyles: { fillColor: [24, 144, 255] },
            });
        }

        doc.save(`forte-settlement-${summaryData.date_from}-to-${summaryData.date_to}.pdf`);
    };

    const buildDetailRows = (settlement, transactions) => {
        const rows = [];
        transactions.forEach((txn) => {
            const settleDate = settlement.forte_settle_date || txn.forte_settle_date || '';
            const fundingId  = settlement.forte_funding_id  || txn.forte_funding_id  || '';
            const batchId    = settlement.forte_settle_batch_id || txn.forte_settle_batch_id || '';
            const participants = txn.participants || [];
            const nonFeeTotal  = Number(((Number(txn.total_checkout) || 0) - (Number(txn.processing_fee) || 0)).toFixed(2));

            if (participants.length === 0) {
                rows.push([settleDate, fundingId, batchId, txn.payer_name || '', '', txn.registration_name || '', nonFeeTotal]);
                return;
            }

            const programEntries = [];
            participants.forEach((participant) => {
                const participantName = `${participant.camper_first_name || ''} ${participant.camper_last_name || ''}`.trim();
                const programs = participant.programs || [];
                if (programs.length === 0) {
                    programEntries.push({ participantName, glCode: '', programName: '', programType: '', baseAmount: 0 });
                    return;
                }
                programs.forEach((program) => {
                    programEntries.push({
                        participantName,
                        glCode: program.gl_code || '',
                        programName: program.program_name || '',
                        programType: program.program_type || '',
                        baseAmount: Number(program.price_at_purchase) || 0,
                    });
                });
            });

            const baseTotal = programEntries.reduce((sum, r) => sum + r.baseAmount, 0);
            const delta     = Number((nonFeeTotal - baseTotal).toFixed(2));
            const mainIdx   = programEntries.findIndex((r) => r.programType === 'main');

            programEntries.forEach((r, idx) => {
                const adjustedAmount = idx === mainIdx
                    ? Number((r.baseAmount + delta).toFixed(2))
                    : r.baseAmount;
                rows.push([settleDate, fundingId, batchId, r.participantName, r.glCode, r.programName, adjustedAmount]);
            });
        });
        return rows;
    };

    const writeDetailExcel = (exportRows, filename) => {
        const grandTotal = Number(exportRows.reduce((sum, r) => sum + (Number(r[6]) || 0), 0).toFixed(2));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([
            ['Settle Date', 'Funding ID', 'Batch ID', 'Name', 'GL Code', 'Program Name', 'Program Amount'],
            ...exportRows,
            [],
            ['', '', '', '', '', 'TOTAL', grandTotal],
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Registration Detail');
        XLSX.writeFile(wb, filename);
    };

    const handleExportRegistrationDetail = async () => {
        if (selectedSettlement) {
            if (!detailRows || detailRows.length === 0) {
                message.warning('No transaction detail found for the selected settlement row.');
                return;
            }
            const rows = buildDetailRows(selectedSettlement, detailRows);
            const safeId = String(selectedSettlement.forte_funding_id || 'selected').replace(/[^a-zA-Z0-9_-]/g, '_');
            writeDetailExcel(rows, `forte-registration-detail-${safeId}.xlsx`);
            return;
        }

        const validSettlements = (summaryData?.settlements || []).filter(hasFundingId);
        if (validSettlements.length === 0) {
            message.warning('No settlements with funding IDs found in this date range.');
            return;
        }

        setExportAllLoading(true);
        try {
            const results = await Promise.all(
                validSettlements.map((s) =>
                    apiForteSettlementTransactions({ token: userDataStore.token, club_id: clubId, funding_id: s.forte_funding_id })
                        .then((res) => ({ settlement: s, transactions: res?.transactions || [] }))
                        .catch(() => ({ settlement: s, transactions: [] }))
                )
            );

            const allRows = results.flatMap(({ settlement, transactions }) =>
                buildDetailRows(settlement, transactions)
            );

            if (allRows.length === 0) {
                message.warning('No registration detail data found for this date range.');
                return;
            }

            const from = summaryData.date_from;
            const to   = summaryData.date_to;
            writeDetailExcel(allRows, `forte-registration-detail-${from}-to-${to}.xlsx`);
        } catch {
            message.error('Failed to export range detail.');
        } finally {
            setExportAllLoading(false);
        }
    };

    return (
        <div>
            <h2 className="report-title">Forte Settlement Report</h2>

            <div style={{ marginTop: 24, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="YYYY-MM-DD"
                />
                <Button type="primary" onClick={handleRun} loading={isBusy}>
                    Run Report
                </Button>
                <Radio.Group
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    buttonStyle="solid"
                    size="small"
                >
                    <Radio.Button value="basic">Basic</Radio.Button>
                    <Radio.Button value="advanced">Advanced</Radio.Button>
                </Radio.Group>
                {summaryData && (
                    <>
                        <Button onClick={handleExportExcel} icon={<span>📊</span>}>
                            Export Excel
                        </Button>
                        <Button onClick={handleExportPdf} icon={<span>📄</span>}>
                            Export PDF
                        </Button>
                        <Button
                            onClick={handleExportRegistrationDetail}
                            loading={exportAllLoading}
                            disabled={detailLoading}
                        >
                            {selectedSettlement ? 'Export Registration Detail' : 'Export Range Detail'}
                        </Button>
                    </>
                )}
            </div>

            {summaryData && (
                <>
                    {totals && (
                        <div style={{
                            padding: '12px 16px',
                            backgroundColor: '#f0f5ff',
                            borderRadius: 8,
                            border: '1px solid #d6e4ff',
                            marginBottom: 16,
                            display: 'flex',
                            gap: 32,
                            flexWrap: 'wrap',
                        }}>
                            <div><strong>Period:</strong> {summaryData.date_from} to {summaryData.date_to}</div>
                            <div><strong>Batches:</strong> {summaryData.settlements.length}</div>
                            <div><strong>Transactions:</strong> {totals.transaction_count}</div>
                            <div><strong>Gross:</strong> {formatCurrency(totals.gross_amount)}</div>
                            <div><strong>BeClutch Net:</strong> {formatCurrency(totals.our_net_amount)}</div>
                            {viewMode === 'advanced' && totals.effective_fee_amount != null && (
                                <div><strong>Forte Fee:</strong> {formatCurrency(totals.effective_fee_amount)}</div>
                            )}
                            {totals.batch_settle != null && (
                                <div><strong>Bank Deposit:</strong> {formatCurrency(totals.batch_settle)}</div>
                            )}
                            {viewMode === 'advanced' && totals.acked_refund != null && parseFloat(totals.acked_refund) !== 0 && (
                                <div style={{ color: '#ff4d4f' }}>
                                    <strong>Acked Refunds:</strong> -{formatCurrency(parseFloat(totals.acked_refund))}
                                </div>
                            )}
                            {viewMode === 'advanced' && totals.acked_deposit != null && parseFloat(totals.acked_deposit) !== 0 && (
                                <div style={{ color: '#52c41a' }}>
                                    <strong>Extra Deposits:</strong> {formatCurrency(parseFloat(totals.acked_deposit))}
                                </div>
                            )}
                            {viewMode === 'advanced' && totals.reg_refund_sum != null && parseFloat(totals.reg_refund_sum) !== 0 && (
                                <div style={{ color: '#ff4d4f' }}>
                                    <strong>Issued Refunds:</strong> -{formatCurrency(parseFloat(totals.reg_refund_sum))}
                                </div>
                            )}
                            {totals.fee_discrepancy != null && (
                                <div style={{ color: totals.fee_discrepancy === 0 ? '#52c41a' : totals.fee_discrepancy > 0 ? '#fa8c16' : '#ff4d4f' }}>
                                    <strong>Fee Diff:</strong> {formatCurrency(totals.fee_discrepancy)}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #eee', padding: 12, overflowX: 'auto' }}>
                        <Table
                            dataSource={summaryData.settlements}
                            columns={viewMode === 'advanced' ? SUMMARY_COLUMNS_ADVANCED : SUMMARY_COLUMNS_BASIC}
                            rowKey={(r, i) => r.forte_funding_id || r.forte_settle_batch_id || `${r.forte_settle_date}-${r.forte_settle_type || 'unknown'}-${i}`}
                            pagination={false}
                            bordered
                            size="middle"
                            rowClassName={(record) => {
                                const isSelected = !!selectedSettlement
                                    && selectedSettlement.forte_funding_id === record.forte_funding_id
                                    && selectedSettlement.forte_settle_date === record.forte_settle_date
                                    && (selectedSettlement.forte_settle_batch_id || '') === (record.forte_settle_batch_id || '');
                                return isSelected ? 'table-row-total' : '';
                            }}
                            onRow={(record) => {
                                const clickable = hasFundingId(record);
                                return {
                                    onClick: clickable ? () => handleRowClick(record) : undefined,
                                    style: { cursor: clickable ? 'pointer' : 'not-allowed' },
                                };
                            }}
                            scroll={{ x: 'max-content' }}
                        />
                    </div>

                    {selectedFunding && (
                        <div style={{ marginTop: 24 }}>
                            <Divider orientation="left" style={{ fontWeight: 600 }}>
                                Transactions — Funding ID: {selectedFunding}
                            </Divider>
                            <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #eee', padding: 12, overflowX: 'auto' }}>
                                <Table
                                    dataSource={detailRows || []}
                                    columns={viewMode === 'advanced' ? DETAIL_COLUMNS_ADVANCED : DETAIL_COLUMNS_BASIC}
                                    rowKey={(r, i) => r.transaction_id || i}
                                    loading={detailLoading}
                                    pagination={{ pageSize: 50 }}
                                    bordered
                                    size="small"
                                    rowClassName={(_, idx) => idx % 2 === 1 ? 'table-row-light' : ''}
                                    scroll={{ x: 'max-content' }}
                                    expandable={{
                                        expandedRowRender: (record) => <ExpandedRow record={record} />,
                                        rowExpandable: (record) => (record.participants?.length ?? 0) > 0,
                                        expandRowByClick: false,
                                    }}
                                />
                            </div>

                            {batchAcknowledged && batchAcknowledged.length > 0 && (
                                <div style={{ marginTop: 16 }}>
                                    <Divider orientation="left" style={{ fontWeight: 600, fontSize: 13 }}>
                                        Acknowledged Adjustments
                                        <span style={{ marginLeft: 8, fontWeight: 400, color: '#8c8c8c', fontSize: 12 }}>
                                            — extra deposits or refunds in this funding batch not tied to active transactions
                                        </span>
                                    </Divider>
                                    <div style={{ backgroundColor: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f', padding: 12, overflowX: 'auto' }}>
                                        <Table
                                            dataSource={batchAcknowledged}
                                            columns={ACK_COLUMNS}
                                            rowKey={(r, i) => r.id || r.transaction_id || i}
                                            pagination={false}
                                            bordered
                                            size="small"
                                            scroll={{ x: 'max-content' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {!summaryData && !isBusy && (
                <div style={{
                    marginTop: 24,
                    padding: '40px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: 8,
                    border: '1px solid #eee',
                    color: '#999',
                    fontSize: 16,
                }}>
                    Select a date range and click Run Report.
                </div>
            )}
        </div>
    );
}

export default ForteSettlementReport;
